import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { AuthGuard as Auth0Guard } from '@auth0/auth0-angular';
import { VehiclesComponent } from './pages/calculations/vehicles/vehicles.component';
import { LogoutComponent } from './pages/logout/logout.component';
import { NotAuthorizedComponent } from './pages/not-authorized/not-authorized.component';
import { LoggedOutComponent } from './pages/logged-out/logged-out.component';
import { LayoutComponent } from './components/layout/layout.component';
import { GeneralComponent } from './pages/account/general/general.component';
import { SecurityComponent } from './pages/account/security/security.component';
import { NotificationsComponent } from './pages/account/notifications/notifications.component';
import { OrganizationSettingsComponent } from './pages/organization/settings/organization-settings.component';
import { OrganizationMembersComponent } from './pages/organization/members/organization-members.component';
import { OrganizationBillingComponent } from './pages/organization/billing/organization-billing.component';
import { MandateCreateComponent } from './pages/mandates/create/mandate-create.component';
import { ClientListComponent } from './pages/clients/list/client-list.component';
import { CreatePersonalComponent } from './pages/clients/create-personal/create-personal.component';
import { CreateCorporateComponent } from './pages/clients/create-corporate/create-corporate.component';
import { OrganizationCreateComponent } from './pages/organization/create/organization-create.component';
import { calculationRoutes } from '../../../modules/src/lib/calculations/calculation.routes';
import { RegisterComponent } from './pages/register/register.component';
import { RootComponent } from './components/root/root.component';
import {
  CreateClientQuestionnaireComponent,
} from './pages/client-questionnaires/create/create-client-questionnaire.component';
import {
  ListClientQuestionnairesComponent,
} from './pages/client-questionnaires/list/list-client-questionnaires.component';
import {
  ClientQuestionnaireKeynameEnum,
} from '../../../../../common/src/enums/client-questionnaire/client-questionnaire-keyname.enum';
import {
  QuebecTaxForm2022ViewClientQuestionnaireComponent,
} from './pages/client-questionnaires/quebec-tax-form-2022/view/quebec-tax-form-2022-view-client-questionnaire.component';
import { advancedAuthRoutes } from '../../../modules/src/lib/advanced-auth/advanced-auth.routes';
import {
  NotifyClientQuestionnaireComponent,
} from './pages/client-questionnaires/notify/notify-client-questionnaire.component';
import { LocaleGuard } from '../../../modules/src/lib/advanced-auth/guards/locale.guard';
import { ErrorComponent } from './pages/error/error.component';
import { SubscriptionSuspendedComponent } from './pages/subscription/suspended/subscription-suspended.component';
import { SubscriptionCreateComponent } from './pages/subscription/create/subscription-create.component';
import { SubscriptionCreatedComponent } from './pages/subscription/created/subscription-created.component';
import { ListInvitesComponent } from './pages/invites/list/list-invites.component';
import { EmployeeSummaryComponent } from './pages/employees/summary/employee-summary.component';
import { AccessGuard } from './guards/access.guard';
import { HasOrganizationGuard } from './guards/has-organization.guard';
import { HasNoOrganizationGuard } from './guards/has-no-organization.guard';
import { OnlyLoginGuard } from './guards/only-login.guard';
import { SubscriptionUpgradeComponent } from './pages/subscription/upgrade/subscription-upgrade.component';
import { HasActivatedLicenseGuard } from './guards/has-activated-license.guard';
import { HasNoActivatedLicenseGuard } from './guards/has-no-activated-license.guard';
import {
  SalaryDividendViewClientQuestionnaireComponent,
} from './pages/client-questionnaires/salary-dividend/view/salary-dividend-view-client-questionnaire.component';

const routes: Routes = [
  {
    path: '',
    component: RootComponent,
    children: [
      {
        path: '',
        component: LayoutComponent,
        canActivate: [LocaleGuard],
        children: [
          {
            path: '',
            component: HomeComponent,
            canActivate: [OnlyLoginGuard, HasOrganizationGuard, HasActivatedLicenseGuard],
          },
          {
            path: 'account',
            canActivate: [OnlyLoginGuard, HasOrganizationGuard, HasActivatedLicenseGuard],
            children: [
              {
                path: 'general',
                component: GeneralComponent,
              },
              {
                path: 'security',
                component: SecurityComponent,
              },
              {
                path: 'notifications',
                component: NotificationsComponent,
              }],
          },
          {
            path: 'organization',
            canActivate: [OnlyLoginGuard, HasOrganizationGuard, HasActivatedLicenseGuard],
            children: [
              {
                path: 'settings',
                component: OrganizationSettingsComponent,
              },
              {
                path: 'members',
                component: OrganizationMembersComponent,
              },
              {
                path: 'billing',
                component: OrganizationBillingComponent,
              },
              {
                path: 'billing/subscription',
                canActivate: [OnlyLoginGuard],
                children: [
                  {
                    path: 'suspended',
                    component: SubscriptionSuspendedComponent,
                  },
                  {
                    path: 'upgrade',
                    component: SubscriptionUpgradeComponent,
                  },
                ],
              },
              {
                path: 'employees',
                component: EmployeeSummaryComponent,
              },
            ],
          }, {
            path: 'mandates',
            canActivate: [AccessGuard],
            children: [
              {
                path: 'create',
                component: MandateCreateComponent,
              },
            ],
          },
          {
            path: 'clients',
            canActivate: [AccessGuard],
            children: [
              {
                path: 'personal',
                children: [
                  {
                    path: '',
                    component: ClientListComponent,
                  },
                  {
                    path: 'create',
                    component: CreatePersonalComponent,
                  },
                ],
              },
              {
                path: 'corporate',
                children: [
                  {
                    path: '',
                    component: ClientListComponent,
                  },
                  {
                    path: 'create',
                    component: CreateCorporateComponent,
                  },
                ],
              },
            ],
          },
          {
            path: 'client-questionnaires',
            canActivate: [AccessGuard],
            children: [
              {
                path: '',
                component: ListClientQuestionnairesComponent,
              },
              {
                path: 'create',
                component: CreateClientQuestionnaireComponent,
              },
              {
                path: ClientQuestionnaireKeynameEnum.QuebecTaxForm2022,
                children: [
                  {
                    path: ':questionnaireId',
                    children: [
                      {
                        path: '',
                        component: QuebecTaxForm2022ViewClientQuestionnaireComponent,
                      },
                      {
                        path: 'notify',
                        component: NotifyClientQuestionnaireComponent,
                      },
                    ],

                  },
                ],
              },
              {
                path: ClientQuestionnaireKeynameEnum.QuebecTaxForm2023,
                children: [
                  {
                    path: ':questionnaireId',
                    children: [
                      {
                        path: '',
                        component: QuebecTaxForm2022ViewClientQuestionnaireComponent,
                      },
                      {
                        path: 'notify',
                        component: NotifyClientQuestionnaireComponent,
                      },
                    ],

                  },
                ],
              },
              {
                path: ClientQuestionnaireKeynameEnum.SalaryDividend,
                children: [
                  {
                    path: ':questionnaireId',
                    children: [
                      {
                        path: '',
                        component: SalaryDividendViewClientQuestionnaireComponent,
                      },
                      {
                        path: 'notify',
                        component: NotifyClientQuestionnaireComponent,
                      },
                    ],

                  },
                ],
              },
            ],
          },

          {
            path: 'error',
            component: ErrorComponent,
          },

          {
            path: '',
            canActivate: [AccessGuard],
            children: [...calculationRoutes],
          },

        ],
      },
      {
        path: 'onboarding',
        canActivate: [LocaleGuard],
        children: [
          {
            path: 'organization/create',
            component: OrganizationCreateComponent,
            pathMatch: 'full',
            canActivate: [HasNoOrganizationGuard],
          },
          {
            path: 'subscription',
            canActivate: [HasOrganizationGuard, HasNoActivatedLicenseGuard],
            children: [
              {
                path: 'create',
                canActivate: [],
                component: SubscriptionCreateComponent,
              },
              {
                path: 'created',
                canActivate: [],
                component: SubscriptionCreatedComponent,
              },
            ],
          },
          {
            path: 'invites',
            component: ListInvitesComponent,
            pathMatch: 'full',
            canActivate: [HasNoOrganizationGuard],
          },

        ],
      },
      {
        canActivate: [LocaleGuard],
        path: 'logout',
        component: LogoutComponent,
      },
      {
        canActivate: [],
        path: 'register',
        component: RegisterComponent,
      },
      {
        canActivate: [LocaleGuard],
        path: 'not-authorized',
        component: NotAuthorizedComponent,
        pathMatch: 'full',
      },
      {
        canActivate: [LocaleGuard],
        path: 'logged-out',
        component: LoggedOutComponent,
        pathMatch: 'full',
      },
      {
        path: 'calculations/vehicles',
        component: VehiclesComponent,
        canActivate: [Auth0Guard, LocaleGuard],
        data: { groups: ['vehicle-calculations'] },
      },
      ...advancedAuthRoutes,
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
